import React, { useState } from 'react';
import { X, Check, Mail } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
interface ChangeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ChangeEmailModal({
  isOpen,
  onClose
}: ChangeEmailModalProps) {
  const {
    user,
    refreshUser
  } = useAuth();
  const [step, setStep] = useState<'input' | 'verify' | 'success'>('input');
  const [newEmail, setNewEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentEmail = user?.email || '';
  const handleRequestChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }
    if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setError('New email must be different from current email');
      setLoading(false);
      return;
    }
    try {
      const {
        error: updateError
      } = await supabase.auth.updateUser({
        email: newEmail
      });
      if (updateError) throw updateError;
      setStep('verify');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const {
        error: verifyError
      } = await supabase.auth.verifyOtp({
        email: newEmail,
        token: otpCode,
        type: 'email_change'
      });
      if (verifyError) throw verifyError;
      await refreshUser();
      await new Promise(resolve => setTimeout(resolve, 500));
      setStep('success');
      setTimeout(() => {
        handleClose();
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };
  const handleResendCode = async () => {
    setError(null);
    setLoading(true);
    try {
      const {
        error: resendError
      } = await supabase.auth.updateUser({
        email: newEmail
      });
      if (resendError) throw resendError;
      const successMsg = 'Verification code resent!';
      setError(successMsg);
      setTimeout(() => setError(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification code');
    } finally {
      setLoading(false);
    }
  };
  const handleClose = () => {
    setStep('input');
    setNewEmail('');
    setOtpCode('');
    setError(null);
    onClose();
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-1707" data-mp="2j9w3nvzimn2b">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" data-id="element-1708" data-mp="2naoe2sk4iy06">
        <div className="flex items-center justify-between mb-6" data-id="element-1709" data-mp="bp5u9il0e8c1">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-1710" data-mp="7naoo1w1p8sg">Change Email</h2>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-1711" data-mp="7pf49h3b3w8k">
            <X size={20} data-id="element-1712" data-mp="3u3ezjtqwagzf" />
          </button>
        </div>
        {step === 'input' && <form onSubmit={handleRequestChange} className="space-y-4" data-id="element-1713" data-mp="3p3kazwule3hj">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4" data-id="element-1714" data-mp="3p1pmy9o0xv98">
              <div className="flex items-start" data-id="element-1715" data-mp="3k3ebjui2zcow">
                <Mail size={20} className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" data-id="element-1716" data-mp="3g2m496srrhv9" />
                <div data-id="element-1717" data-mp="3b5lvup8g630z">
                  <p className="text-sm text-blue-900 font-semibold mb-1" data-id="element-1718" data-mp="3b6446k8dw7p9">
                    Current Email
                  </p>
                  <p className="text-sm text-blue-700" data-id="element-1719" data-mp="19cxs8erw5dko">{currentEmail}</p>
                </div>
              </div>
            </div>
            <div data-id="element-1720" data-mp="6321daozhajb">
              <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-1721" data-mp="62jw5okcflz9">
                New Email Address
              </label>
              <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Enter new email address" required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-1722" data-mp="3t6gh38d2k130" />
              <p className="text-xs text-gray-500 mt-2" data-id="element-1723" data-mp="3afyvroukg7ro">
                We'll send a verification code to this email address
              </p>
            </div>
            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800" data-id="element-1724" data-mp="3ef7mekub7pj0">
                {error}
              </div>}
            <div className="flex gap-3 pt-2" data-id="element-1725" data-mp="35fkk7wtrafow">
              <button type="button" onClick={handleClose} className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-colors" data-id="element-1726" data-mp="35iqd7n5ia1pw">
                Cancel
              </button>
              <button type="submit" disabled={loading || !newEmail} className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm" data-id="element-1727" data-mp="2vukxbf2dko8p">
                {loading ? 'Sending...' : 'Send Code'}
              </button>
            </div>
          </form>}
        {step === 'verify' && <form onSubmit={handleVerifyOtp} className="space-y-4" data-id="element-1728" data-mp="39kepo7d39qn6">
            <div className="text-center mb-4" data-id="element-1729" data-mp="3nkfpdjhmas12">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1730" data-mp="3nl8yus7ynvmw">
                <Mail size={32} className="text-red-600" data-id="element-1731" data-mp="3jkfdo6lwkuxp" />
              </div>
              <p className="text-gray-700 text-sm mb-2" data-id="element-1732" data-mp="3qn3nr0d4l6hs">
                We sent a verification code to:
              </p>
              <p className="font-semibold text-gray-900" data-id="element-1733" data-mp="3vlnfxiibbeil">{newEmail}</p>
            </div>
            <div data-id="element-1734" data-mp="4e2id1m3ldmq">
              <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-1735" data-mp="4dja6cm923lw">
                Verification Code
              </label>
              <input type="text" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-center text-2xl tracking-widest" data-id="element-1736" data-mp="d7ee5hbmxnzy" />
              <p className="text-xs text-gray-500 mt-2" data-id="element-1737" data-mp="2tv9wzzd38tyt">
                Enter the 6-digit code from your email
              </p>
            </div>
            {error && <div className={`rounded-xl p-3 text-sm border ${error.includes('resent') ? 'bg-green-50 text-green-800 border-green-200' : 'bg-red-50 text-red-800 border-red-200'}`} data-id="element-1738" data-mp="1b2i1iuv4bsps">
                {error}
              </div>}
            <div className="flex gap-3 pt-2" data-id="element-1739" data-mp="xatqyw0ipcax">
              <button type="button" onClick={() => {
            setStep('input');
            setOtpCode('');
            setError(null);
          }} className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-colors" data-id="element-1740" data-mp="1tz9jn0yxc1s9">
                Back
              </button>
              <button type="submit" disabled={loading || otpCode.length !== 6} className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm" data-id="element-1741" data-mp="9jengzx08oy3">
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
            <button type="button" onClick={handleResendCode} disabled={loading} className="w-full text-red-600 text-sm font-medium hover:underline disabled:opacity-50" data-id="element-1742" data-mp="1uowyugi3bjsh">
              Didn't receive the code? Resend
            </button>
          </form>}
        {step === 'success' && <div className="text-center py-8" data-id="element-1743" data-mp="2sei1img0yz6h">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1744" data-mp="2doatz1rk30kk">
              <Check size={32} className="text-green-600" data-id="element-1745" data-mp="2ilkhgbvdx25r" />
            </div>
            <h3 className="text-lg font-bold mb-2 text-gray-900" data-id="element-1746" data-mp="36bojwfwu6vv6">
              Email Changed!
            </h3>
            <p className="text-gray-600 text-sm" data-id="element-1747" data-mp="3b36jazj7ooe1">
              Your email has been successfully updated to
            </p>
            <p className="text-red-600 font-semibold mt-1" data-id="element-1748" data-mp="zmd3os2nb5lt">{newEmail}</p>
            <p className="text-xs text-gray-500 mt-4" data-id="element-1749" data-mp="upmn83mxi7zv">Refreshing...</p>
          </div>}
      </div>
    </div>;
}